import { useRef, useState } from "react";
import { filesFromEntries, hasSkillMd, isJunkPath, relPath, zipFolder } from "@/lib/zip";

// Shared archive upload control — a dashed drag-&-drop zone with an inline selected-file state.
// Used by the New Skill flow and the Suggest flow so both look and behave identically.
// Validation (extension + optional size) happens here; the parent only ever receives a valid File.
//
// When `allowFolder` is set, the user can also pick or drop a plain folder: we zip it in-browser
// (preserving relative paths) and hand the parent a single .zip File, which flows through the exact
// same upload path as a hand-made archive — the backend already extracts zips server-side.

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

type Props = {
  file: File | null;
  onPick: (f: File | null) => void;
  accept: string; // the <input accept> attribute
  extensions: string[]; // allowed lowercase suffixes, e.g. [".tar", ".tgz"]
  maxBytes?: number; // optional client-side size cap (backend still enforces its own)
  hint?: string; // subtext shown under the call-to-action
  allowFolder?: boolean; // also allow picking/dropping a folder (zipped client-side)
};

export default function FileDropzone({ file, onPick, accept, extensions, maxBytes, hint, allowFolder }: Props) {
  const inputRef = useRef<HTMLInputElement>(null);
  const folderRef = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);
  const [busy, setBusy] = useState(false); // zipping a folder can take a beat
  const [error, setError] = useState<string | null>(null);
  // When the selection came from a folder, show the folder name + file count instead of the
  // internal "<name>.zip" we hand to the parent (the zip is just how it's transported).
  const [folder, setFolder] = useState<{ name: string; count: number } | null>(null);

  function choose(f: File | null) {
    if (!f) return;
    const name = f.name.toLowerCase();
    if (!extensions.some((e) => name.endsWith(e))) {
      setError(`Unsupported file — choose a ${extensions.join(", ")} archive.`);
      return;
    }
    if (maxBytes && f.size > maxBytes) {
      setError(`That archive is ${formatBytes(f.size)} — the limit is ${formatBytes(maxBytes)}.`);
      return;
    }
    setError(null);
    setFolder(null); // this is a real archive, not a folder
    onPick(f);
  }

  // Turn a picked/dropped folder (a flat list of files carrying relative paths) into a .zip File.
  async function handleFolder(files: File[]) {
    const usable = files.filter((f) => !isJunkPath(relPath(f)));
    if (!usable.length) {
      setError("That folder is empty — nothing to upload.");
      return;
    }
    if (!hasSkillMd(usable)) {
      setError("No SKILL.md found in that folder — a skill needs a SKILL.md at its root.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      // Name the archive after the top-level folder for a friendly selected-file label.
      const top = relPath(usable[0]).split("/")[0] || "skill";
      const zipped = await zipFolder(usable, `${top}.zip`);
      if (maxBytes && zipped.size > maxBytes) {
        setError(`That folder is ${formatBytes(zipped.size)} zipped — the limit is ${formatBytes(maxBytes)}.`);
        return;
      }
      setFolder({ name: top, count: usable.length });
      onPick(zipped);
    } catch {
      setError("Couldn't read that folder. Try a .zip or .tar archive instead.");
    } finally {
      setBusy(false);
    }
  }

  async function onDrop(e: React.DragEvent) {
    e.preventDefault();
    setDragging(false);
    // Capture what we need synchronously — the DataTransfer is neutered after the first await.
    const firstFile = e.dataTransfer.files?.length ? e.dataTransfer.files[0] : null;
    if (allowFolder && e.dataTransfer.items?.length) {
      const entries = Array.from(e.dataTransfer.items)
        .map((it) => (it as unknown as { webkitGetAsEntry?: () => unknown }).webkitGetAsEntry?.())
        .filter(Boolean) as Parameters<typeof filesFromEntries>[0];
      if (entries.some((en) => (en as { isDirectory?: boolean }).isDirectory)) {
        await handleFolder(await filesFromEntries(entries));
        return;
      }
    }
    choose(firstFile);
  }

  function onFolderInput(e: React.ChangeEvent<HTMLInputElement>) {
    const files = e.target.files ? Array.from(e.target.files) : [];
    if (files.length) void handleFolder(files);
    if (folderRef.current) folderRef.current.value = ""; // allow re-picking the same folder
  }

  function remove(e: React.MouseEvent) {
    e.preventDefault();
    onPick(null);
    setError(null);
    setFolder(null);
    if (inputRef.current) inputRef.current.value = "";
    if (folderRef.current) folderRef.current.value = "";
  }

  return (
    <>
      <label
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={onDrop}
        className={`group flex cursor-pointer flex-col items-center justify-center rounded-2xl border-2 border-dashed px-6 py-10 text-center transition ${
          dragging
            ? "border-primary bg-primary/10"
            : error
              ? "border-red-500/40 bg-red-500/[0.03]"
              : file
                ? "border-primary/40 bg-primary/[0.05]"
                : "border-white/15 bg-white/[0.015] hover:border-primary/40 hover:bg-white/[0.03]"
        }`}
      >
        <input
          ref={inputRef}
          type="file"
          accept={accept}
          className="hidden"
          onChange={(e) => choose(e.target.files?.[0] ?? null)}
        />
        {busy ? (
          <>
            <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary/15 text-primary">
              <Spinner />
            </div>
            <p className="mt-3 font-medium text-text/85">Preparing folder…</p>
            <p className="mt-0.5 text-xs text-text/45">Packaging your files for upload</p>
          </>
        ) : file ? (
          <>
            <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary/15 text-primary">
              {folder ? <FolderIcon /> : <FileIcon />}
            </div>
            <p className="mt-3 font-medium text-text">{folder ? folder.name : file.name}</p>
            <p className="mt-0.5 text-xs text-text/50">
              {folder
                ? `Folder · ${folder.count} file${folder.count === 1 ? "" : "s"} · ${formatBytes(file.size)}`
                : `${formatBytes(file.size)} · click to replace`}
            </p>
            <button
              type="button"
              onClick={remove}
              className="mt-3 text-xs text-text/50 underline-offset-2 transition hover:text-red-300 hover:underline"
            >
              Remove
            </button>
          </>
        ) : (
          <>
            <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-white/[0.04] text-text/60 transition group-hover:bg-primary/15 group-hover:text-primary">
              <UploadIcon />
            </div>
            <p className="mt-3 font-medium text-text/85">
              Drag &amp; drop, or <span className="text-primary">browse</span>
            </p>
            {hint && <p className="mt-0.5 text-xs text-text/45">{hint}</p>}
            {allowFolder && (
              <button
                type="button"
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); folderRef.current?.click(); }}
                className="mt-3 rounded-lg border border-white/15 px-3 py-1.5 text-xs font-medium text-text/70 transition hover:border-primary/40 hover:text-text"
              >
                Or select a folder — no zipping needed
              </button>
            )}
          </>
        )}
      </label>

      {/* Directory picker (kept outside the label so it isn't triggered by label clicks). */}
      {allowFolder && (
        <input
          ref={folderRef}
          type="file"
          className="hidden"
          multiple
          // Non-standard attributes for directory selection; supported by Chromium/WebKit/Firefox.
          {...({ webkitdirectory: "", directory: "" } as Record<string, string>)}
          onChange={onFolderInput}
        />
      )}

      {error && <p className="mt-2 text-xs text-red-400">{error}</p>}
    </>
  );
}

const ic = "h-5 w-5";
function UploadIcon() {
  return (
    <svg className={ic} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 16V4M7 9l5-5 5 5" />
      <path d="M5 16v2a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-2" />
    </svg>
  );
}
function FileIcon() {
  return (
    <svg className={ic} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <path d="M14 2v6h6" />
    </svg>
  );
}
function FolderIcon() {
  return (
    <svg className={ic} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 20a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h5l2 3h7a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2z" />
    </svg>
  );
}
function Spinner() {
  return (
    <svg className={`${ic} animate-spin`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <path d="M21 12a9 9 0 1 1-6.219-8.56" />
    </svg>
  );
}
